import { Injectable } from '@angular/core';
import { DataService } from '../data.service';
import { Store } from '@ngrx/store';
import { AfasProject, CalculationApi, mapAfasProject, mapMetaToProject, Project } from 'src/app/types';
import { BehaviorSubject, combineLatest, firstValueFrom, lastValueFrom, map, Subject, switchMap, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ProjectCalculationService {
    /**
     * The list of AFAS projects that are loaded from the server
     *
     * @memberof ProjectCalculationService
     */
    afasProjectList$ = new BehaviorSubject<AfasProject[]>([]);

    /**
     * The list of all calculations from backend
     *
     * @memberof ProjectCalculationService
     */
    calculationList$ = new BehaviorSubject<CalculationApi[]>([]);

    /**
     * The list of projects that are loaded from the server
     * It combines the AFAS projects with the calculations
     *
     * @memberof ProjectCalculationService
     */
    projectList$ = combineLatest([
        this.afasProjectList$,
        this.calculationList$
    ]).pipe(
        map(([afasProjects, calculations]) => {
            const allProjectIds = new Set<string>(afasProjects.map(project => project.Projectnummer_));
            const calculationsWithoutProject = calculations.filter(calc => !allProjectIds.has(calc.meta.projectId || ''));
            const missingProjectIds = [...new Set<string>(calculationsWithoutProject.map(calc => calc.meta.projectId))];

            return [
                // Open projects
                ...afasProjects.map<Project>(project => ({
                    ...mapAfasProject(project),
                    calculations: calculations.filter(calc => calc.meta.projectId === project.Projectnummer_)
                        .map(calc => ({
                            ...calc,
                            meta: {
                                ...calc.meta,
                                versionDate: isNaN(new Date(calc.meta.versionDate).valueOf()) ? new Date(project.Datum_toegevoegd).toISOString() : calc.meta.versionDate,
                            }
                        }))
                })),
                // Closed projects
                ...missingProjectIds.map<Project>(project => ({
                    // Derive project from first calculation's meta data
                    ...mapMetaToProject(calculations.find(calc => calc.meta.projectId === project)),
                    active: false,
                    calculations: calculationsWithoutProject
                        // All calculations that have this project id
                        .filter(calc => calc.meta.projectId === project)
                        // Ignore invalid calculations (no version date)
                        .filter(c => !isNaN(new Date(c.meta.versionDate)?.valueOf()))
                        .sort((a, b) => a.meta.templateName === b.meta.templateName ? (a.meta.versionDate ?? '') < (b.meta.versionDate ?? '') ? -1 : 1 : a.meta.templateName > b.meta.templateName ? 1 : -1)
                })),
            ]
                .sort((a, b) => {
                    // check if id consists only of digits
                    const aIsNum = /^\d+$/.test(a.id)
                    const bIsNum = /^\d+$/.test(b.id)

                    if (aIsNum && bIsNum) {
                        // both numeric → compare numerically, descending
                        const na = Number(a.id)
                        const nb = Number(b.id)
                        return nb - na
                    }

                    if (aIsNum) {
                        // a is numeric, b is not → a comes before b
                        return -1
                    }

                    if (bIsNum) {
                        // b is numeric, a is not → b comes before a
                        return 1
                    }

                    // beide non-numeric → behoud originele volgorde (stable sort) 
                    return 0
                })
        }));

    constructor(
        private dataService: DataService,
        private store: Store,
    ) {
        this.onInit();
    }

    onInit(): void {
        this.refreshProjects();
        this.refreshCalculations();
    }

    public refreshCalculations(): void {
        this.dataService.getCalculations().subscribe(
            (calculations: CalculationApi[]) => {
                this.calculationList$.next(calculations);
            }
        );
    }

    public refreshProjects(): void {
        this.dataService.getAfasProjects().subscribe((projects: AfasProject[]) => {
            this.afasProjectList$.next(projects);
        });
    }

    public async getProjectById(id: string) {
        return await firstValueFrom(this.projectList$.pipe(
            map(projects => projects?.find(p => p.id === id))
        ));
    }
}
